import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// next
import { useRouter } from 'next/router';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
// paths
import { PATH_TODO } from '../../../routes/paths';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useAuth from '../../../hooks/useAuth';
// utils
import axios from '../../../utils/axios';
// swr
import { mutate } from 'swr';

// ----------------------------------------------------------------------

export default function ContractDoneDialog({open, closeDialog, depositGuide}) {
  const { user } = useAuth();
  const router = useRouter();
  const smUp = useResponsive('up', 'sm');
  const [step, setStep] = useState("todo");  // todo, check -> done
  const [decision, setDecision] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const hasStep = depositGuide?.candidate_left_in_progress;

  const goTodo = () => {
    router.push({
      pathname: PATH_TODO.detail,
      query: { c: depositGuide?.crew_id },
    });
  };

  const stopAll = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = depositGuide?.job_post_id;
    try {
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/stop/`, {reason: "이미 채용을 완료했어요"});
      enqueueSnackbar('공고를 마감하였습니다.');
      mutate(`/api/company/${companyId}/job-posts/`);
      closeDialog();
    } catch (error) {
      enqueueSnackbar('공고 마감에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    } finally {
      setIsSubmit(false);
    }
  };

  const stopOrClose = () => {
    if (decision === "stop") {
      stopAll();
    } else if (decision === "keep") {
      closeDialog();
    }
  }

  useEffect(() => {
    if (open) {
      setDecision("");
      setIsSubmit(false);
      setStep(hasStep ? "check" : "todo");
    }
  }, [open]);

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      <DialogTitle gutterBottom>
        {step === "done" ? "공고를 계속 열어두고 추가 후보자를 받아볼까요?" : "🎉 계약 시작하기"}
      </DialogTitle>
      {step === "done" ? (
        <DialogContent>
          <Stack spacing={2}>
            <Stack
              sx={{
                borderRadius: 1,
                p: 2,
                "&:hover": {background: (theme) => theme.palette.grey[200], cursor: "pointer"},
                border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                ...(decision === "stop" && {background: (theme) => theme.palette.grey[200]}),
              }}
              direction="row"
              spacing={1}
              alignItems={"flex-start"}
              onClick={() => setDecision("stop")}
            >
              <SvgIconStyle src="/icons/icon-minus-circle-mono.svg" sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20 }} />
              <Stack spacing={.5}>
                <Typography variant="subtitle2">
                  마감하기
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  이번 계약으로 충분해요. 예약된 미팅과 다른 매칭을 취소할게요.
                </Typography>
              </Stack>
            </Stack>
            <Stack
              sx={{
                borderRadius: 1,
                p: 2,
                "&:hover": {background: (theme) => theme.palette.grey[200], cursor: "pointer"},
                border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                ...(decision === "keep" && {background: (theme) => theme.palette.grey[200]}),
              }}
              direction="row"
              spacing={1}
              alignItems={"flex-start"}
              onClick={() => { setDecision("keep"); } }
            >
              <SvgIconStyle src="/icons/icon-user-check-mono.svg" sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20 }} />
              <Stack spacing={.5}>
                <Typography variant="subtitle2">
                  계속 받기
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  다른 후보자들도 확인하고 추가 계약을 진행할 수 있어요.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
      ) : (
        <DialogContent>
          <DialogContentText>
            계약금을 입금하고 {depositGuide?.nickname} 크루와 업무를 시작하세요
          </DialogContentText>

          <Box
            sx={{
              mt: 3,
              borderRadius: 2,
              backgroundColor: 'grey.200',
              p: 2.5,
            }}
          >
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} gutterBottom>
              1️⃣ 계약서 서명 부탁드립니다
            </Typography>
            <Typography variant={smUp ? "body1" : "body2"} paragraph>
              메일로 보내드린 표준 용역 계약서에 서명 부탁드립니다.
            </Typography>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} gutterBottom>
              2️⃣ 계약금을 입금해주세요
            </Typography>
            <Typography variant={smUp ? "body1" : "body2"} paragraph>
              - 송금 기한 : {depositGuide?.deposit_date} <br/>
              - 계약 기간 : {depositGuide?.contract_date} <br/>
              - 계좌 : 신한은행 140-013-710652 <br/>
              - 예금주 : 주식회사 스페이스와이 <br/>
              - 금액 : {depositGuide?.amount} <br/>
            </Typography>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
              📌 크루의 연락처는 [계약금 입금]이 확인된 후에 전달됩니다.
            </Typography>
          </Box>
        </DialogContent>
      )}

      <DialogActions>
        {step === "todo" && (
          <Button
            variant="contained"
            onClick={goTodo}
          >
            첫 업무 전달하기
          </Button>
        )}
        {step === "check" && (
          <Button
            variant="contained"
            onClick={() => setStep("done")}
          >
            확인했어요
          </Button>
        )}
        {step === "done" && (
          <LoadingButton
            variant="contained"
            onClick={stopOrClose}
            disabled={!decision}
            loading={isSubmit}
          >
            {decision === "keep" ? "매칭 계속 받기"
            : decision === "stop" ? "매칭 마감하기"
            : "답변을 선택해주세요"}
          </LoadingButton>
        )}
      </DialogActions>

    </Dialog>
  )
}
